/** Thin fetch wrapper with CSRF header and 401 handling. */

/** The session queryFn already maps its own 401 to `{authenticated: false}`,
 * so apiFetch skips this path to avoid duplicating the work. */
export const SESSION_PATH = "/auth/session";

export const SESSION_QUERY_KEY = ["auth", "session"] as const;

let csrfToken: string | null = null;

/**
 * Slug of the active organization. Updated by `useAuth` whenever the session
 * resolves; consumed by `orgPath` so callers can write
 * `apiJson(orgPath('/admin/repositories'))` without threading the slug
 * through props.
 */
let currentOrgSlug: string | null = null;

export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

export function setCurrentOrgSlug(slug: string | null) {
  currentOrgSlug = slug;
}

export function getCurrentOrgSlug(): string | null {
  return currentOrgSlug;
}

/**
 * Invoked whenever `apiFetch` sees a 401 from a non-session endpoint. Lets the
 * query layer flip the cached session to unauthenticated so route guards can
 * redirect to `/login` instead of letting the user navigate over dead routes.
 */
type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null) {
  unauthorizedHandler = handler;
}

/**
 * Prefix the given API path with the current org's slug. Throws if no org is
 * active; callers that need the org slug should not be rendered before the
 * session resolves.
 *
 * `path` should start with `/` and be relative to `/api/orgs/{slug}/`.
 * Example: `orgPath('/admin/repositories')` -> `/api/orgs/acme/admin/repositories`.
 */
export function orgPath(path: string): string {
  if (!currentOrgSlug) {
    throw new Error("orgPath called before active org was resolved");
  }
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `/api/orgs/${encodeURIComponent(currentOrgSlug)}${normalized}`;
}

export interface ApiFetchOptions extends RequestInit {
  /** Set when the caller wants to handle 401 itself (e.g. logout, which is
   * already on its way to /login). Suppresses the global session-flip. */
  suppressUnauthorized?: boolean;
}

export async function apiFetch(
  path: string,
  options: ApiFetchOptions = {},
): Promise<Response> {
  const { suppressUnauthorized, ...init } = options;
  const headers = new Headers(init.headers);

  if (
    csrfToken &&
    init.method &&
    ["POST", "PUT", "PATCH", "DELETE"].includes(init.method.toUpperCase())
  ) {
    headers.set("X-CSRF-Token", csrfToken);
  }

  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(path, {
    ...init,
    headers,
    credentials: "same-origin",
  });

  if (
    response.status === 401 &&
    path !== SESSION_PATH &&
    !suppressUnauthorized
  ) {
    unauthorizedHandler?.();
  }

  return response;
}

export async function apiJson<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const response = await apiFetch(path, options);
  if (!response.ok) {
    throw new ApiError(response.status, await response.text());
  }
  return response.json();
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string,
  ) {
    super(friendlyMessageFromBody(body) ?? `API error ${status}`);
    this.name = "ApiError";
  }
}

/** The backend returns `{"error": "..."}` on most failures. Pull that string
 * out so callers can show `e.message` directly without unwrap boilerplate. */
function friendlyMessageFromBody(body: string): string | null {
  if (!body) return null;
  try {
    const parsed = JSON.parse(body) as { error?: unknown };
    if (typeof parsed.error === "string" && parsed.error) return parsed.error;
  } catch {
    // Not JSON; fall through.
  }
  return body;
}
